import Enumerable from "linq";
import { MuscularStrengthInputKinds, MuscularStrengthValueKinds, Section } from "../../../../domain/document";
import { IItem, IDrawableItem, IAlternative, GraphItem, TextBoxItem, RangeOfMotionItem } from "../../../../domain/document/items";
import { Geometry } from "../../../../domain/drawing";
import { HeightRangePair, LimitPair } from "../../../../domain/system";
import { AbstractAlternator } from "./AbstractAlternator";

export class RangeOfMotionItemAlternator extends AbstractAlternator {
  protected createAlternativeItems(items: IItem[], sections: Section[]): (IAlternative)[] {
    // 関節可動域の名称のキー(graph_*)はページごとで採番される模様
    // 同一フォーマット内の複数ページに関節可動域が置かれた場合は重複するので注意
    return Enumerable.from(items)
      .where(i => this.shouldAlternate(i))
      .groupBy(i => this.getSectionNumber(i, sections))
      .selectMany(g => this.createAlternativeItemsForSection(g.toArray()))
      .toArray();
  }

  private getSectionNumber(item: IItem, sections: Section[]): number {
    for (let i = 0; i < sections.length; ++i) {
      const section = sections[i];
      if (!section || !section.itemPrivateIds) { continue; }
      if (section.itemPrivateIds.indexOf(item.privateId) >= 0) {
        return i;
      }
    }
    console.warn("関節可動域が配置されたページの特定に失敗しました。");
    return -1;
  }

  private createAlternativeItemsForSection(items: IItem[]): IAlternative[] {
    return Enumerable.from(items)
      .groupBy(i => this.toName(i))
      .select(g => this.toAlternativeItem(g.toArray()))
      .where(i => !!i)
      .toArray();
  }

  private shouldAlternate(item: IItem): boolean {
    return !!this.toKey(item);
  }

  private toKey(item: IItem): { prefix: string, index: number } {
    const source = this.toKeySource(item);
    return this.isKey(source)
      ? this.createKey(source)
      : null;
  }

  private toKeySource(item: IItem): string {
    if (item instanceof GraphItem) {
      return item.name;
    } else if (item instanceof TextBoxItem) {
      return item.groupKey;
    } else {
      return null;
    }
  }

  private isKey(key: string): boolean {
    return /^graph_[0-9]+\[[0-4]\]$/.test(key);
  }

  private createKey(source: string): { prefix: string, index: number } {
    const pair = source.slice(0, -1).split("[");
    return {
      prefix: pair[0],
      index: Number(pair[1])
    };
  }

  private toName(item: IItem): string {
    return this.toKey(item).prefix;
  }

  private toAlternativeItem(items: IItem[]): IAlternative {
    // H大で間接可動域のオブジェクトがおかしくなってるフォーマットが現存.
    // ・MMT高の名称がずれていて組合せがおかしい.
    // 　->ずれてる最初の関節可動域にはMMT高がない状態に.
    //   ->余った最後のMMT高はどの関節可動域と関連づかない.
    //   ->上記以外はずれてるものの文書作成には問題ない.(MMT高の値は参照されてないため.)
    // v1を見る限り上記フォーマット作成はできないはずで、できてしまった経緯は不明.
    // (クラッシュ時に中途半端に保存された？特定バージョンのバグ？誰かが手で直した？)
    // フォーマットは修正されるので今後作成される文書では問題起きないが、作成済文書は救済する必要がある.
    // 以下の通り対処する.
    // ・オブジェクトが揃ってなくても関節可動域として扱う.
    //   ->足りないオブジェクトは仮のオブジェクトを追加して5つ揃ってるように振る舞わせる.
    //     ->仮のオブジェクトは、通常ありえないisVisible=falseとして編集画面には表示しない.
    //     ->仮のオブジェクトは、通常ありえないisAdditional=trueとしてバックエンドには返さない.
    const contents = this.extractContents(items);
    const firstContent = Enumerable.from([contents.graph, contents.lowerMmt, contents.higherMmt, contents.lowerRom, contents.higherRom])
      .firstOrDefault(c => !!c);
    return firstContent
      ? new RangeOfMotionItem(
        this.orInvisibleGraph(contents.graph, firstContent),
        this.orInvisibleTextBox(contents.lowerMmt, firstContent.geometry),
        this.orInvisibleTextBox(contents.higherMmt, firstContent.geometry),
        this.orInvisibleTextBox(contents.lowerRom, firstContent.geometry),
        this.orInvisibleTextBox(contents.higherRom, firstContent.geometry)
      )
      : null;
  }

  private extractContents(items: IItem[]): { graph: GraphItem, lowerMmt: TextBoxItem, higherMmt: TextBoxItem, lowerRom: TextBoxItem, higherRom: TextBoxItem } {
    return {
      graph: this.findGraph(items),
      lowerMmt: this.findTextBox(items, 1),
      higherMmt: this.findTextBox(items, 2),
      lowerRom: this.findTextBox(items, 3),
      higherRom: this.findTextBox(items, 4),
    };
  }

  private findGraph(items: IItem[]): GraphItem {
    return Enumerable.from(items).ofType<GraphItem>(GraphItem)
      .firstOrDefault(i => this.toKey(i).index === 0);
  }

  private findTextBox(items: IItem[], index: number): TextBoxItem {
    return Enumerable.from(items).ofType<TextBoxItem>(TextBoxItem)
      .firstOrDefault(i => this.toKey(i).index === index);
  }

  private orInvisibleGraph(source: GraphItem, firstContent: GraphItem | TextBoxItem): GraphItem {
    return this.or(source, () => this.createInvisibleGraph(firstContent));
  }

  private or<T>(source: T, createDefault: () => T) {
    return source
      ? source
      : createDefault();
  }

  private orInvisibleTextBox(source: TextBoxItem, geometry: Geometry): TextBoxItem {
    return this.or(source, () => this.createInvisibleTextBox(geometry));
  }

  private createInvisibleGraph(firstContent: GraphItem | TextBoxItem): GraphItem {
    const item = new GraphItem();
    // 最低限必要な情報を設定.値を取りようがないので規定値.(v1も取れないだろうから同様のはず...)
    item.id = firstContent.id;
    item.privateId = firstContent.privateId;
    item.name = this.toKey(firstContent).prefix + "[0]";
    item.rangeOfValue = new HeightRangePair<number>(0, 0);
    item.limitOfValue = new LimitPair<number>(0, 0);
    item.muscularStrengthInputKind = MuscularStrengthInputKinds.Keyboard;
    item.muscularStrengthValueKind = MuscularStrengthValueKinds.Symbol;
    this.setupInvisible(item, firstContent.geometry);
    return item;
  }

  private setupInvisible(source: IDrawableItem, geometry: Geometry): void {
    source.geometry = geometry.clone();
    source.isVisible = false;
    source.isAdditional = true;
  }

  private createInvisibleTextBox(geometry: Geometry): TextBoxItem {
    const item = new TextBoxItem("");
    this.setupInvisible(item, geometry);
    return item;
  }
}
